import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Monitor, CheckCircle, XCircle, Clock, Edit2, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DeviceManagement = ({ userId, userName }) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDevice, setEditingDevice] = useState(null);
  const [newLabel, setNewLabel] = useState('');

  useEffect(() => {
    if (userId) {
      fetchDevices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API}/admin/usuarios/${userId}/devices`);
      setDevices(response.data);
    } catch (error) {
      toast.error('Erro ao carregar dispositivos');
      console.error('[DEVICE_MANAGEMENT] Error fetching devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAllowDevice = async (deviceId) => {
    try {
      await axiosInstance.post(`${API}/admin/usuarios/${userId}/devices/${deviceId}/allow`);
      toast.success('Dispositivo liberado com sucesso');
      fetchDevices();
    } catch (error) {
      toast.error('Erro ao liberar dispositivo');
      console.error('[DEVICE_MANAGEMENT] Error allowing device:', error);
    }
  };

  const handleBlockDevice = async (deviceId) => {
    if (!window.confirm('Tem certeza que deseja bloquear este dispositivo?')) return;
    
    try {
      await axiosInstance.post(`${API}/admin/usuarios/${userId}/devices/${deviceId}/block`);
      toast.success('Dispositivo bloqueado com sucesso');
      fetchDevices();
    } catch (error) {
      toast.error('Erro ao bloquear dispositivo');
      console.error('[DEVICE_MANAGEMENT] Error blocking device:', error);
    }
  };

  const handleEditLabel = (device) => {
    setEditingDevice(device);
    setNewLabel(device.label || '');
  };

  const handleSaveLabel = async () => {
    if (!newLabel.trim()) {
      toast.error('Digite um nome para o dispositivo');
      return;
    }

    try {
      await axiosInstance.put(`${API}/admin/usuarios/${userId}/devices/${editingDevice.deviceId}/label`, {
        label: newLabel
      });
      toast.success('Nome do dispositivo atualizado');
      setEditingDevice(null);
      setNewLabel('');
      fetchDevices();
    } catch (error) {
      toast.error('Erro ao atualizar nome do dispositivo');
      console.error('[DEVICE_MANAGEMENT] Error updating label:', error);
    }
  };

  const handleDeleteDevice = async (deviceId, deviceLabel) => {
    if (!window.confirm(`Tem certeza que deseja excluir o dispositivo "${deviceLabel || 'Sem nome'}"?`)) {
      return;
    }

    try {
      await axiosInstance.delete(`${API}/admin/usuarios/${userId}/devices/${deviceId}`);
      toast.success('Dispositivo excluído com sucesso');
      fetchDevices();
    } catch (error) {
      toast.error('Erro ao excluir dispositivo');
      console.error('[DEVICE_MANAGEMENT] Error deleting device:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      allowed: { icon: CheckCircle, label: 'Permitido', className: 'bg-green-100 border-green-300', color: '#000' },
      blocked: { icon: XCircle, label: 'Bloqueado', className: 'bg-red-100 border-red-300', color: '#000' },
      pending: { icon: Clock, label: 'Pendente', className: 'bg-yellow-100 border-yellow-300', color: '#000' }
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${badge.className}`} style={{ color: badge.color }}>
        <Icon className="w-3 h-3" style={{ color: badge.color }} />
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const allowedCount = devices.filter(d => d.status === 'allowed').length;
  const blockedCount = devices.filter(d => d.status === 'blocked').length;
  const pendingCount = devices.filter(d => d.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" style={{ color: '#000' }}>
      {/* Header with statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ color: '#000' }}>
            <Monitor className="w-5 h-5" style={{ color: '#000' }} />
            Dispositivos de {userName}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-2xl font-bold" style={{ color: '#16a34a' }}>{allowedCount}</div>
              <div className="text-sm" style={{ color: '#16a34a' }}>Permitidos</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-2xl font-bold" style={{ color: '#dc2626' }}>{blockedCount}</div>
              <div className="text-sm" style={{ color: '#dc2626' }}>Bloqueados</div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="text-2xl font-bold" style={{ color: '#ca8a04' }}>{pendingCount}</div>
              <div className="text-sm" style={{ color: '#ca8a04' }}>Pendentes</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Device list */}
      {devices.length === 0 ? (
        <Card>
          <CardContent className="py-8">
            <div className="text-center" style={{ color: '#6b7280' }}>
              <Monitor className="w-12 h-12 mx-auto mb-2" style={{ color: '#9ca3af' }} />
              <p>Nenhum dispositivo registrado para este usuário</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {devices.map((device) => (
            <Card key={device.id}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Monitor className="w-5 h-5" style={{ color: '#6b7280' }} />
                      <div>
                        <h3 className="font-semibold text-lg" style={{ color: '#000' }}>{device.label || 'Dispositivo sem nome'}</h3>
                        <p className="text-xs font-mono" style={{ color: '#6b7280' }}>
                          ID: {device.deviceId.substring(0, 16)}...
                        </p>
                      </div>
                    </div>
                    
                    <div className="ml-8 space-y-1 text-sm" style={{ color: '#4b5563' }}>
                      <div className="flex gap-4">
                        <span><strong style={{ color: '#000' }}>Status:</strong> {getStatusBadge(device.status)}</span>
                      </div>
                      <div><strong style={{ color: '#000' }}>Primeira vez:</strong> {formatDate(device.firstSeenAt)}</div>
                      <div><strong style={{ color: '#000' }}>Última vez:</strong> {formatDate(device.lastSeenAt)}</div>
                      {device.lastIp && <div><strong style={{ color: '#000' }}>Último IP:</strong> {device.lastIp}</div>}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    {device.status === 'allowed' ? (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleBlockDevice(device.deviceId)}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Bloquear
                      </Button>
                    ) : (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleAllowDevice(device.deviceId)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Liberar
                      </Button>
                    )}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditLabel(device)}
                      style={{ color: '#000' }}
                    >
                      <Edit2 className="w-4 h-4 mr-1" style={{ color: '#000' }} />
                      Renomear
                    </Button>
                    
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteDevice(device.deviceId, device.label)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Excluir
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Label Dialog */}
      <Dialog open={!!editingDevice} onOpenChange={() => setEditingDevice(null)}>
        <DialogContent style={{ color: '#000' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#000' }}>Renomear Dispositivo</DialogTitle>
            <DialogDescription style={{ color: '#4b5563' }}>
              Digite um nome amigável para identificar este dispositivo
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="label" style={{ color: '#000' }}>Nome do dispositivo</Label>
              <Input
                id="label"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Ex: Notebook do trabalho, Celular pessoal..."
                style={{ color: '#fff' }}
                className="placeholder:text-gray-400"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingDevice(null)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveLabel}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeviceManagement;
